/**
 * Filter Option Fetchers
 *
 * Reusable filter option fetching functions to reduce duplication
 * across RPM filter configurations.
 */

import { SelectOption } from '../../../interfaces/common';
import { PayerItem } from '../../../interfaces/eligibility';
import coreService from '../../../services/core';
import eligibilityService from '../../../services/eligibility';
import { BillingService } from '../../../services/billing';
import { RPMClaimStatus } from '../../../interfaces/billing';
import { RPMCptCodes } from '../types/rpms.types';

/**
 * Creates a client-side filter function for static lists
 * (e.g., clinics, CPT codes that don't require API calls)
 *
 * @param options - Static list of options to filter
 * @returns Async function that filters options based on search term
 */
export function createClientSideFilterFetcher(
  options: SelectOption[]
): (searchTerm: string) => Promise<SelectOption[]> {
  return async (searchTerm: string) => {
    try {
      // If no search term, return all options
      if (!searchTerm || searchTerm.trim() === '') {
        return options;
      }
      // Filter options based on search term
      const lowerSearchTerm = searchTerm.toLowerCase();
      return options.filter(option =>
        option.label.toLowerCase().includes(lowerSearchTerm) ||
        option.value.toLowerCase().includes(lowerSearchTerm)
      );
    } catch (error) {
      console.error('Error filtering options:', error);
      return [];
    }
  };
}

/**
 * Fetches clinic options with client-side filtering
 */
export function createClinicFilterFetcher(
  clinicOptions: SelectOption[]
): (searchTerm: string) => Promise<SelectOption[]> {
  return createClientSideFilterFetcher(clinicOptions);
}

/**
 * Fetches insurance company options from the API
 */
export async function fetchInsuranceCompanyOptions(searchTerm: string): Promise<SelectOption[]> {
  try {
    const response = await eligibilityService.getPayers({
      filter: searchTerm || null,
    });
    const options =
      response?.data?.map((payer: PayerItem) => ({
        label: `(${payer.id}) ${payer.name}`,
        value: payer.id,
      })) || [];
    return options;
  } catch (error) {
    console.error('Error fetching insurance companies:', error);
    return [];
  }
}

/**
 * Fetches CPT code options with client-side filtering
 */
export async function fetchCptCodeOptions(searchTerm: string): Promise<SelectOption[]> {
  try {
    const cptOptions = Object.values(RPMCptCodes).map((value) => ({
      value,
      label: value
    }));

    // If no search term, return all CPT codes
    if (!searchTerm || searchTerm.trim() === '') {
      return cptOptions;
    }

    // Filter CPT codes based on search term
    const lowerSearchTerm = searchTerm.toLowerCase();
    return cptOptions.filter(cpt =>
      cpt.label.toLowerCase().includes(lowerSearchTerm) ||
      cpt.value.toLowerCase().includes(lowerSearchTerm)
    );
  } catch (error) {
    console.error('Error filtering CPT codes:', error);
    return [];
  }
}

/**
 * Fetches member options from the API (requires minimum search length)
 */
export async function fetchMemberOptions(searchTerm: string): Promise<SelectOption[]> {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }

    const response = await coreService.fetchMembers({
      searchTerm: searchTerm.trim(),
      target: 'members',
    });

    const options =
      response?.data?.map((member) => ({
        label: `${member.fullName} ID: ${member.id}`,
        value: member.uuid,
      })) || [];
    return options;
  } catch (error) {
    console.error('Error fetching members:', error);
    return [];
  }
}

/**
 * Fetches process ID options from the API
 */
export async function fetchProcessIdOptions(searchTerm: string): Promise<SelectOption[]> {
  try {
    const billingService = new BillingService();
    const processes = await billingService.getProcesses();

    if (!processes) {
      return [];
    }

    // Show all processes (no filter by status - include both CREATED and EXPORTED)
    let processOptions = processes.map((process) => ({
      label: `Process ${process.id} - ${process.status} (${new Date(process.createdAt).toLocaleDateString()})`,
      value: process.id.toString(),
    }));

    // If search term provided, filter the options
    if (searchTerm && searchTerm.trim() !== '') {
      const lowerSearchTerm = searchTerm.toLowerCase();
      processOptions = processOptions.filter(option =>
        option.label.toLowerCase().includes(lowerSearchTerm) ||
        option.value.includes(searchTerm)
      );
    }

    return processOptions;
  } catch (error) {
    console.error('Error fetching process IDs:', error);
    return [];
  }
}

/**
 * Fetches claim status options with client-side filtering
 */
export async function fetchClaimStatusOptions(searchTerm: string): Promise<SelectOption[]> {
  try {
    const statusOptions = [
      // Created statuses
      { value: RPMClaimStatus.CREATED, label: 'Created (Ready)' },
      { value: RPMClaimStatus.CREATED_WITH_WARNINGS, label: 'Created with Warnings' },
      { value: RPMClaimStatus.CREATED_WITH_ERRORS, label: 'Created with Errors' },
      // Exported statuses
      { value: RPMClaimStatus.EXPORTED, label: 'Exported' },
      { value: RPMClaimStatus.EXPORTED_WITH_WARNINGS, label: 'Exported with Warnings' },
      { value: RPMClaimStatus.NOT_EXPORTED, label: 'Not Exported' },
    ];

    // If no search term, return all status options
    if (!searchTerm || searchTerm.trim() === '') {
      return statusOptions;
    }

    // Filter status options based on search term
    const lowerSearchTerm = searchTerm.toLowerCase();
    return statusOptions.filter(status =>
      status.label.toLowerCase().includes(lowerSearchTerm) ||
      status.value.toLowerCase().includes(lowerSearchTerm)
    );
  } catch (error) {
    console.error('Error filtering claim status options:', error);
    return [];
  }
}
